/**
 * SurfacedCandidatesPlaceholder.jsx — RC EMPLOYER.IA.SLICE-1.
 *
 * Honest placeholder for the future Channel-2 surface (anonymous
 * matching → employer interest signals → candidate-controlled
 * profile reveal). This component renders inside the "Surfaced
 * Candidates" tab on `/employer/roles/{id}`.
 *
 * STRICT OPERATIONAL HONESTY:
 *   • No fake candidate cards. No fabricated counts.
 *   • No backend call. No data fetch. Pure presentation.
 *   • Wording is roadmap-oriented, not "live feature" oriented.
 *
 * Operator visibility:
 *   • A small internal note appears for the operator account ONLY.
 *     It surfaces the fact that operator-only preview access exists
 *     at /employer/roles/{id}/matches — no hidden API is called from
 *     here.
 *
 * Out of scope (deferred):
 *   • Wiring `GET /api/employer/roles/{id}/matches`
 *   • Card list rendering
 *   • Drawer / reveal flow
 *   • Signal composer
 */
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Sparkles, ShieldCheck, UserCheck, Inbox,
} from 'lucide-react';

import { useIsOperator } from '@/lib/useIsOperator';


const ROADMAP_STAGES = [
  {
    icon: Sparkles,
    title: 'NDORFlow surfaces candidates',
    body: (
      'Anonymous, role-scoped matches are produced from your role '
      + 'definition and the wider set of eligible candidates. '
      + 'Identity is not revealed at this stage.'
    ),
  },
  {
    icon: Inbox,
    title: 'You express interest',
    body: (
      'For candidates worth a closer look, you send a structured '
      + 'interest signal — visible to the candidate, but PII-free '
      + 'in your view.'
    ),
  },
  {
    icon: UserCheck,
    title: 'The candidate decides',
    body: (
      'The candidate accepts, declines, or defers. Their identity '
      + 'is shared with you only on explicit acceptance, scoped to '
      + 'this role.'
    ),
  },
  {
    icon: ShieldCheck,
    title: 'You review revealed profiles',
    body: (
      'Approved profile fields appear inline — name, headline, '
      + 'location, current role, summary, skills. No contact '
      + 'details. No CV download. Auditable by design.'
    ),
  },
];


export default function SurfacedCandidatesPlaceholder({ roleId }) {
  // RC 1.62 Phase D completion (D-3) — operator flag is now
  // server-projected; the frontend no longer carries a hardcoded
  // UUID/email.
  const { isOperator } = useIsOperator();
  const showOperatorNote = isOperator;

  return (
    <div
      className="space-y-5"
      data-testid="surfaced-candidates-placeholder"
    >
      <Card
        className="border-ndor-edge bg-ndor-surface"
        data-testid="surfaced-candidates-preview-card"
      >
        <CardContent className="px-5 py-5 sm:px-6 sm:py-6">
          <div className="flex items-start gap-4">
            <div
              className="shrink-0 w-10 h-10 rounded-lg border border-ndor-edge bg-ndor-surface-alt/60 flex items-center justify-center"
              aria-hidden
            >
              <Sparkles className="h-4 w-4 text-ndor-ink-secondary" />
            </div>
            <div className="min-w-0 flex-1 space-y-2">
              <div className="flex flex-wrap items-center gap-2">
                <h2 className="text-base font-semibold text-ndor-ink tracking-tight">
                  NDOR-Surfaced Candidates
                </h2>
                <Badge
                  variant="outline"
                  className="border-ndor-edge bg-ndor-surface-alt/60 text-[10px] uppercase tracking-[0.14em] text-ndor-ink-secondary"
                  data-testid="surfaced-candidates-preview-pill"
                >
                  Available for enabled workspaces
                </Badge>
              </div>
              <p className="text-sm text-ndor-ink-secondary leading-relaxed">
                NDORFlow can surface anonymous, role-scoped candidate
                matches at publish time and let you express interest
                before any identity is shared. This capability is
                built and live — it activates per workspace via an
                entitlement gate.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <div
        className="grid grid-cols-1 md:grid-cols-2 gap-3"
        data-testid="surfaced-candidates-roadmap"
      >
        {ROADMAP_STAGES.map((s, i) => {
          const Icon = s.icon;
          return (
            <div
              key={s.title}
              className="rounded-md border border-ndor-edge bg-ndor-bg/40 px-4 py-4 space-y-2"
              data-testid={`surfaced-candidates-roadmap-stage-${i + 1}`}
            >
              <div className="flex items-center gap-2">
                <Icon className="h-4 w-4 text-ndor-ink-secondary" aria-hidden />
                <p className="text-sm font-medium text-ndor-ink">
                  {i + 1}. {s.title}
                </p>
              </div>
              <p className="text-xs text-ndor-ink-secondary leading-relaxed">
                {s.body}
              </p>
            </div>
          );
        })}
      </div>

      <p
        className="text-[11px] text-ndor-ink-muted leading-relaxed text-center pt-2"
        data-testid="surfaced-candidates-disclaimer"
      >
        Until this is enabled for your workspace, evidence-led
        decisions are made through Imported Candidates — the
        surface live for your workspace today.
      </p>

      {showOperatorNote ? (
        <div
          className="rounded-md border border-dashed border-ndor-edge bg-ndor-surface-alt/40 px-4 py-3 text-[11px] text-ndor-ink-muted"
          data-testid="surfaced-candidates-operator-note"
        >
          <span className="font-mono uppercase tracking-[0.18em] text-[10px] text-ndor-ink-secondary mr-2">
            Operator
          </span>
          Operator preview access is enabled internally. The anonymous
          matches surface lives at{' '}
          <code className="font-mono text-ndor-ink">
            /employer/roles/{roleId || ':id'}/matches
          </code>{' '}
          and is not exposed to other employer accounts.
        </div>
      ) : null}
    </div>
  );
}
